import React, { useMemo, useState } from 'react';
import Wrapper from 'components/layouts/Wrapper/Wrapper';
import { Headers, LoadingSpinner } from 'components/core';
import { Button } from 'components/core/Button/Button';
import ListEmpty from '../../Common/ListEmpty/ListEmpty';
import { useGetBuyerRefundsQuery } from 'services/reportsApi';
import PropertyFilter from './PropertyFilter';
import RefundsSummary from './RefundsSummary';
import RefundsTable from './RefundsTable';
import { summarizeRefunds, uniqueProperties } from './summarize';
import { csvFilename, toCsv, triggerCsvDownload } from './csvExport';

export const RefundsContainer: React.FC = () => {
  const { data, isLoading, isSuccess } = useGetBuyerRefundsQuery();
  const [propertyId, setPropertyId] = useState<string | null>(null);

  const rows = data?.rows ?? [];
  const properties = useMemo(() => uniqueProperties(rows), [rows]);
  const filteredRows = useMemo(
    () => (propertyId ? rows.filter((r) => r.property_id === propertyId) : rows),
    [rows, propertyId]
  );
  const summary = useMemo(() => summarizeRefunds(filteredRows), [filteredRows]);

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    triggerCsvDownload(toCsv(filteredRows), csvFilename());
  };

  return (
    <Wrapper additionalClasses="flex-col items-start" pageTitle="Refund Report">
      {isLoading && <LoadingSpinner />}
      <Headers headingContent="Refund Report" />
      <p className="text-14s text-gray-600">
        Payments returned to you <strong>after</strong> they were sent to the vendor — for example,
        double-pays or vendor-flagged invalid invoices.
      </p>
      {isSuccess && rows.length === 0 && <ListEmpty subText="No refunds yet." />}
      {isSuccess && rows.length > 0 && (
        <>
          <div className="flex w-full items-center justify-between">
            <PropertyFilter properties={properties} value={propertyId} onChange={setPropertyId} />
            <Button
              variants="extraSmall"
              buttonLabel="Export CSV"
              isDisabled={filteredRows.length === 0}
              onClick={handleExport}
              additionalClasses="h-9 px-4"
            />
          </div>
          <RefundsSummary summary={summary} />
          {filteredRows.length === 0 ? (
            <ListEmpty subText="No refunds for this property." />
          ) : (
            <RefundsTable rows={filteredRows} />
          )}
        </>
      )}
    </Wrapper>
  );
};

export default RefundsContainer;
